import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { VERTICALS } from "@postman/core/verticals";
import { Accent } from "@/components/pipeline";
import { meQueryOptions } from "@/lib/session";
import { coordQuery } from "@/lib/coord";

/**
 * The coordinator shell for one vertical.
 *
 * `beforeLoad` checks the grant the session already carries, so someone with
 * no business here is bounced before a screen paints. That is navigation, not
 * security — the grant is re-resolved against the database by the
 * `coordinator` macro on every one of these API routes, and an AI/ML
 * coordinator curling /api/coord/neurotech gets a 403 whatever this
 * file does. See api/src/routes/pipeline.test.ts, which proves it at the route.
 */
export const Route = createFileRoute("/_auth/coord/$vertical")({
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    const mine = me?.coordinates ?? [];
    const allowed = me?.user.role === "admin" || mine.includes(params.vertical);
    if (allowed) return;
    // Somewhere they can actually stand. `/dashboard` is the obvious target
    // and the wrong one: it sits under `_onboarded`, so a coordinator who has
    // never uploaded a resume gets handed the student onboarding form with no
    // explanation of why. Their own vertical says what happened by arriving.
    throw redirect(
      mine.length > 0
        ? { to: "/coord/$vertical", params: { vertical: mine[0] } }
        : { to: "/dashboard" },
    );
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(coordQuery(params.vertical)),
  component: CoordShell,
});

const BY_SLUG = new Map(VERTICALS.map((v) => [v.slug, v]));

const tab = "mono text-ink-3 transition-colors hover:text-ink [&.active]:text-ink";
const activeProps = { className: "active" };

function CoordShell() {
  const { vertical } = Route.useParams();
  const meta = BY_SLUG.get(vertical as never);

  return (
    <Accent slug={vertical} className="flex flex-col gap-10">
      <div className="flex flex-col gap-5">
        <Link to="/dashboard" className="mono text-ink-3 hover:text-ink">
          ← Dashboard
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div className="flex flex-col gap-2">
            <p className="mono text-ink-3">Coordinating</p>
            <h1 className="display text-[clamp(28px,4vw,40px)]">
              {meta?.name ?? vertical}
            </h1>
          </div>
          <nav className="flex items-center gap-6 pb-1">
            <Link
              to="/coord/$vertical"
              params={{ vertical }}
              activeOptions={{ exact: true }}
              className={tab}
              activeProps={activeProps}
            >
              Applicants
            </Link>
            <Link
              to="/coord/$vertical/rounds"
              params={{ vertical }}
              className={tab}
              activeProps={activeProps}
            >
              Rounds &amp; form
            </Link>
          </nav>
        </div>
      </div>

      <Outlet />
    </Accent>
  );
}
